import { cn } from "ui";
import loader from "@monaco-editor/loader";
import { useField } from "../hooks/useField";
import { useFormFieldContext } from "./FormField";
import { Editor } from "@monaco-editor/react";
import { useTheme } from "next-themes";

loader.config({
  paths: { vs: "/monaco/vs" },
});

interface JsonFormFieldProps {
  className?: string;
}

export function JsonFormField({ className }: JsonFormFieldProps) {
  const {
    name,
    disabled,
    readOnly,
    required,
    defaultValue = "{}",
    validation,
    customValidation,
    descriptionId,
    errorMessageId,
    shouldUnregister,
  } = useFormFieldContext();

  const { ref, value, onChange, onBlur, error } = useField({
    name,
    defaultValue,
    validation,
    customValidation,
    shouldUnregister,
  });
  const { theme } = useTheme();

  return (
    <div
      tabIndex={-1}
      ref={ref}
      className={cn(
        "overflow-hidden rounded-lg border border-foundations-subtle focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-info-bold",
        error &&
          "focus-within:border-danger-bold focus-within:ring-danger-bold",
        className
      )}
    >
      <Editor
        wrapperProps={{
          "aria-required": required,
          "aria-invalid": !!error,
          "aria-describedby": !error
            ? `${descriptionId}`
            : `${descriptionId} ${errorMessageId}`,
          onBlur,
        }}
        theme={theme === "dark" ? "vs-dark" : undefined}
        language="json"
        value={value}
        width="100%"
        className={`
      min-h-[180px]
      [&_.lines-content]:border-l [&_.lines-content]:border-l-foundations-subtle
      [&_.monaco-editor]:[--vscode-editor-background:theme(backgroundColor.foundations-default)]
      [&_.monaco-editor]:[--vscode-editorGutter-background:theme(backgroundColor.foundations-subtlest)]
        [&_.monaco-editor]:[--vscode-editorLineNumber-activeForeground:theme(textColor.foundations-boldest)]
        [&_.monaco-editor]:[--vscode-editorLineNumber-foreground:theme(textColor.foundations-subtler)]`}
        onChange={(v) => onChange?.(v || "")}
        options={{
          readOnly: readOnly || disabled,
          scrollbar: {
            vertical: "hidden",
            horizontal: "hidden",
            alwaysConsumeMouseWheel: false,
          },
          autoClosingQuotes: "always",
          minimap: { enabled: false },
          tabSize: 2,
          padding: {
            top: 8,
            bottom: 8,
          },
          wordWrap: "on",
          scrollBeyondLastLine: false,
          hideCursorInOverviewRuler: true,
          guides: { indentation: false },
          wrappingIndent: "indent",
          lineNumbersMinChars: 3,
          suggest: {
            showProperties: false,
          },
        }}
      />
    </div>
  );
}
